import { forwardRef, ReactElement, Ref } from 'react';
import { Box, Dialog, DialogProps, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { usePhoneLayout } from '../../hooks/usePhoneLayout';
import { dialogSurface } from './dialogSurface';

const BottomDrawerTransition = forwardRef(function BottomDrawerTransition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function ResponsiveDialog({
  children,
  contentSx,
  fullScreenOnPhone = false,
  embedded = false,
  ...props
}: DialogProps & {
  contentSx?: object;
  fullScreenOnPhone?: boolean;
  embedded?: boolean;
}) {
  const phone = usePhoneLayout();
  // The menu owns the dialog shell when navigating between embedded views.
  if (embedded) return props.open ? <>{children}</> : null;
  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      {...props}
      fullScreen={phone && fullScreenOnPhone}
      TransitionComponent={
        phone ? BottomDrawerTransition : props.TransitionComponent
      }
      sx={{
        ...(phone && {
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
          },
          ...(phone &&
            fullScreenOnPhone && {
              '& .MuiDialog-container': { alignItems: 'stretch' },
              '& .MuiDialog-paper': { borderRadius: 0 },
            }),
        }),
        ...props.sx,
      }}
      PaperProps={{
        ...props.PaperProps,
        sx: {
          ...dialogSurface(phone),
          m: phone ? 0 : 2,
          ...(phone &&
            fullScreenOnPhone && {
              borderRadius: 0,
              pt: 'env(safe-area-inset-top)',
            }),
          ...(phone &&
            !fullScreenOnPhone && {
              width: '100%',
              maxWidth: 'none',
            }),
          ...props.PaperProps?.sx,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          p: 0,
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Dialog>
  );
}
